import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";
import { useI18n } from "../contexts/I18nContext";
import { resolveUrl } from "../lib/api";
import { fetchOffersCount } from "../lib/offersCache";
import NewsletterSignup from "./NewsletterSignup";
import { Phone, Mail, MapPin } from "lucide-react";

const BASE_NAV = [
  { href: "/#fleet",     ro: "Masini",     en: "Fleet",       always: true },
  { href: "/oferte",     ro: "Oferte",     en: "Offers",      always: false },
  { href: "/despre",     ro: "Despre noi", en: "About us",    always: true },
  { href: "/reviews",    ro: "Recenzii",   en: "Reviews",     always: true },
  { href: "/comparator", ro: "Comparator", en: "Comparator",  always: true },
];

export default function PublicFooter() {
  const { settings } = useSettings();
  const { lang } = useI18n();
  const [hasOffers, setHasOffers] = useState(true);
  const brandName = settings?.brand_name || "Inchirieri Auto Pascani";
  const contact   = settings?.contact || {};
  const seo       = settings?.seo || {};
  const tagline   = lang === "ro" ? settings?.tagline_ro : settings?.tagline_en;
  const year      = new Date().getFullYear();
  const NAV       = BASE_NAV.filter((l) => l.always || hasOffers);

  useEffect(() => {
    fetchOffersCount().then((n) => setHasOffers(n > 0));
  }, []);

  const socials = [
    { key: "facebook",  label: "Facebook",  url: seo.social_facebook },
    { key: "instagram", label: "Instagram", url: seo.social_instagram },
    { key: "youtube",   label: "YouTube",   url: seo.social_youtube },
    { key: "tiktok",    label: "TikTok",    url: seo.social_tiktok },
    { key: "linkedin",  label: "LinkedIn",  url: seo.social_linkedin },
  ].filter((s) => s.url);

  return (
    <footer id="contact" style={{ borderTop: "1px solid var(--brand-border)" }} data-testid="public-footer">

      {/* Gold pinstripe — mirrors the header */}
      <div style={{ height: 1, background: "linear-gradient(90deg, transparent 0%, var(--brand-primary) 25%, var(--brand-primary) 75%, transparent 100%)", opacity: 0.38 }} />

      {/* ── Brand statement zone ── */}
      <div className="relative overflow-hidden py-20 sm:py-28">

        {/* Ghost watermark */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          aria-hidden="true"
        >
          <span
            className="font-serif font-light italic whitespace-nowrap"
            style={{
              fontSize: "clamp(3.5rem, 16vw, 15rem)",
              color: "rgba(212,175,55,0.05)",
              lineHeight: 1,
              letterSpacing: "-0.03em",
            }}
          >
            {brandName}
          </span>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col items-center text-center">

          {/* Logo or name */}
          {settings?.logo_path && settings?.logo_only_mode ? (
            <img src={resolveUrl(settings.logo_path)} alt={brandName} className="h-12 mb-5" />
          ) : (
            <h2
              className="font-serif font-light italic mb-2"
              style={{ fontSize: "clamp(1.6rem, 4vw, 3.2rem)", color: "var(--brand-primary)", letterSpacing: "0.025em" }}
            >
              {brandName}
            </h2>
          )}

          {tagline && (
            <p className="label-eyebrow mb-10" style={{ color: "var(--brand-text-secondary)", opacity: 0.65 }}>
              {tagline}
            </p>
          )}

          {/* Contact row */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3.5">
            {contact.phone && (
              <a
                href={`tel:${contact.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-sm transition-colors hover:text-[var(--brand-primary)]"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="footer-phone"
              >
                <Phone className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-primary)", opacity: 0.75 }} />
                {contact.phone}
              </a>
            )}
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="flex items-center gap-2 text-sm transition-colors hover:text-[var(--brand-primary)]"
                style={{ color: "var(--brand-text-secondary)" }}
                data-testid="footer-email"
              >
                <Mail className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-primary)", opacity: 0.75 }} />
                {contact.email}
              </a>
            )}
            {contact.address && (
              <span className="flex items-center gap-2 text-sm" style={{ color: "var(--brand-text-secondary)" }}>
                <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-primary)", opacity: 0.75 }} />
                {contact.address}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Links + Newsletter ── */}
      <div style={{ borderTop: "1px solid var(--brand-border)" }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 grid sm:grid-cols-2 md:grid-cols-3 gap-10 lg:gap-16">

          {/* Pages */}
          <div>
            <div className="label-eyebrow mb-5">
              {lang === "en" ? "Navigate" : "Pagini"}
            </div>
            <ul className="space-y-3">
              {NAV.map((p) => (
                <li key={p.href}>
                  <Link
                    to={p.href}
                    className="text-sm transition-colors hover:text-[var(--brand-primary)]"
                    style={{ color: "var(--brand-text-secondary)" }}
                  >
                    {lang === "en" ? p.en : p.ro}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div className="label-eyebrow mb-5">Legal</div>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/termeni"
                  className="text-sm transition-colors hover:text-[var(--brand-primary)]"
                  style={{ color: "var(--brand-text-secondary)" }}
                  data-testid="footer-terms-link"
                >
                  {lang === "en" ? "Terms & Conditions" : "Termeni si conditii"}
                </Link>
              </li>
              <li>
                <Link
                  to="/confidentialitate"
                  className="text-sm transition-colors hover:text-[var(--brand-primary)]"
                  style={{ color: "var(--brand-text-secondary)" }}
                  data-testid="footer-privacy-link"
                >
                  {lang === "en" ? "Privacy Policy" : "Confidentialitate"}
                </Link>
              </li>
              <li>
                <Link
                  to="/despre"
                  className="text-sm transition-colors hover:text-[var(--brand-primary)]"
                  style={{ color: "var(--brand-text-secondary)" }}
                  data-testid="footer-about-link"
                >
                  {lang === "en" ? "About us" : "Despre noi"}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="sm:col-span-2 md:col-span-1">
            <div className="label-eyebrow mb-5">
              {lang === "en" ? "Newsletter" : "Aboneaza-te"}
            </div>
            <NewsletterSignup compact />
          </div>
        </div>
      </div>

      {/* ── Bottom strip ── */}
      <div style={{ borderTop: "1px solid var(--brand-border)" }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-between gap-4">
          <span
            style={{
              fontSize: "0.65rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--brand-text-secondary)",
              opacity: 0.5,
            }}
          >
            © {year} {brandName}. All rights reserved.
          </span>

          {socials.length > 0 && (
            <div className="flex items-center gap-5">
              {socials.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="label-eyebrow transition-colors hover:text-[var(--brand-primary)]"
                  style={{ color: "var(--brand-text-secondary)", opacity: 0.55, fontSize: "0.6rem" }}
                  data-testid={`footer-social-${s.key}`}
                >
                  {s.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
